import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type {
  Appointment,
  AppointmentType,
  AppointmentFilters,
  TimeSlot,
  BookAppointmentInput,
  CalendarViewMode,
  AppointmentStatus,
} from '../types/appointments';

interface AppointmentsState {
  appointments: Appointment[];
  appointmentTypes: AppointmentType[];
  availableSlots: TimeSlot[];
  selectedDate: Date;
  viewMode: CalendarViewMode;
  filters: AppointmentFilters;
  loading: boolean;
  slotsLoading: boolean;
  error: string | null;
  selectedAppointment: Appointment | null;

  fetchAppointments: (filters?: AppointmentFilters) => Promise<void>;
  fetchAppointmentTypes: () => Promise<void>;
  fetchAvailableSlots: (typeId: string, dateFrom: string, dateTo: string, includeOffHours?: boolean) => Promise<void>;
  createAppointment: (data: BookAppointmentInput) => Promise<Appointment | null>;
  cancelAppointment: (id: string, reason?: string) => Promise<boolean>;
  rescheduleAppointment: (id: string, newStartTime: string) => Promise<boolean>;
  updateAppointmentStatus: (id: string, status: AppointmentStatus) => Promise<boolean>;
  updateAppointmentNotes: (id: string, notes: string) => Promise<boolean>;
  getAppointment: (id: string) => Promise<Appointment | null>;
  setSelectedDate: (date: Date) => void;
  setViewMode: (mode: CalendarViewMode) => void;
  setFilters: (filters: AppointmentFilters) => void;
  setSelectedAppointment: (appointment: Appointment | null) => void;
  clearError: () => void;
}

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
};

const getBaseUrl = () => {
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
};

export const useAppointmentsStore = create<AppointmentsState>((set, get) => ({
  appointments: [],
  appointmentTypes: [],
  availableSlots: [],
  selectedDate: new Date(),
  viewMode: 'week',
  filters: {},
  loading: false,
  slotsLoading: false,
  error: null,
  selectedAppointment: null,

  fetchAppointments: async (filters?: AppointmentFilters) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const appliedFilters = filters || get().filters;

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'list',
          ...appliedFilters,
          limit: 100,
        }),
      });

      const data = await response.json();

      console.log('[AppointmentsStore] fetchAppointments filters:', JSON.stringify({ action: 'list', ...appliedFilters, limit: 100 }));
      console.log('[AppointmentsStore] fetchAppointments response:', response.status, 'count:', data.appointments?.length);
      console.log('[AppointmentsStore] appointments:', data.appointments?.map((a: { id: string; title: string; start_time: string; owner_id: string | null }) => ({ id: a.id, title: a.title, start: a.start_time, owner_id: a.owner_id })));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch appointments');
      }

      set({ appointments: data.appointments || [], loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch appointments';
      set({ error: message, loading: false });
    }
  },

  fetchAppointmentTypes: async () => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'get_types' }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch appointment types');
      }

      set({ appointmentTypes: data.types || [] });
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
    }
  },

  fetchAvailableSlots: async (typeId: string, dateFrom: string, dateTo: string, includeOffHours?: boolean) => {
    set({ slotsLoading: true, availableSlots: [] });
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-availability`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appointment_type_id: typeId,
          date_from: dateFrom,
          date_to: dateTo,
          ...(includeOffHours ? { include_off_hours: true } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch available slots');
      }

      set({ availableSlots: data.available_slots || [], slotsLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch slots';
      set({ error: message, slotsLoading: false });
    }
  },

  createAppointment: async (input: BookAppointmentInput) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-book`, {
        method: 'POST',
        headers,
        body: JSON.stringify(input),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create appointment');
      }

      await get().fetchAppointments();
      set({ loading: false });
      return data.appointment;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create appointment';
      set({ error: message, loading: false });
      return null;
    }
  },

  cancelAppointment: async (id: string, reason?: string) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'cancel',
          appointment_id: id,
          reason,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel appointment');
      }

      await get().fetchAppointments();
      set({ loading: false });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel appointment';
      set({ error: message, loading: false });
      return false;
    }
  },

  rescheduleAppointment: async (id: string, newStartTime: string) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'reschedule',
          appointment_id: id,
          new_start_time: newStartTime,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reschedule appointment');
      }

      await get().fetchAppointments();
      set({ loading: false });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reschedule';
      set({ error: message, loading: false });
      return false;
    }
  },

  updateAppointmentStatus: async (id: string, status: AppointmentStatus) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'update_status',
          appointment_id: id,
          status,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update status');
      }

      await get().fetchAppointments();
      set({ loading: false });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update status';
      set({ error: message, loading: false });
      return false;
    }
  },

  updateAppointmentNotes: async (id: string, notes: string) => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'update_notes',
          appointment_id: id,
          meeting_notes: notes,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update notes');
      }

      const appointments = get().appointments.map((apt) =>
        apt.id === id ? { ...apt, meeting_notes: notes } : apt
      );
      set({ appointments });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update notes';
      set({ error: message });
      return false;
    }
  },

  getAppointment: async (id: string) => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${getBaseUrl()}/appointments-manage`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'get',
          appointment_id: id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get appointment');
      }

      return data.appointment;
    } catch (error) {
      console.error('Failed to get appointment:', error);
      return null;
    }
  },

  setSelectedDate: (date: Date) => {
    set({ selectedDate: date });
  },

  setViewMode: (mode: CalendarViewMode) => {
    set({ viewMode: mode });
  },

  setFilters: (filters: AppointmentFilters) => {
    set({ filters });
  },

  setSelectedAppointment: (appointment: Appointment | null) => {
    set({ selectedAppointment: appointment });
  },

  clearError: () => {
    set({ error: null });
  },
}));
